/**
 * Module: api/pantene
 * Role: React Query hooks for Pantene video pre-filter API (FTR-963).
 *       Covers upload-url, analyze trigger, and status polling.
 *
 * Uses: ./client:apiFetch, @tanstack/react-query
 * Used by: screens/pantene/VideoUpload.tsx, screens/pantene/ModerationStatus.tsx
 *
 * NOTE: TypeScript types below are a manual mirror of the Pydantic source-of-truth
 * at src/api/v2/buyer/_pantene_schemas.py. Keep in sync when the Python models change.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { apiFetch } from './client';

// ---------------------------------------------------------------------------
// Types — manual mirror of src/api/v2/buyer/_pantene_schemas.py
// ---------------------------------------------------------------------------

export interface UploadUrlRequest {
  filename: string;
  size_bytes: number;
  content_type: string;
}

export interface UploadUrlResponse {
  upload_url: string;
  object_path: string;
  expires_at: string; // ISO-8601 datetime
}

export interface AnalyzeVideoRequest {
  object_path: string;
  stop_words_override?: string[];
}

export interface AnalyzeVideoResponse {
  job_id: string;    // UUID
  accepted_at: string; // ISO-8601 datetime
}

export type JobStage =
  | 'queued'
  | 'extracting'
  | 'transcribing'
  | 'analyzing'
  | 'done'
  | 'failed';

export type Verdict = 'pass' | 'fail' | 'gray';

export interface StatusResponse {
  stage: JobStage;
  verdict: Verdict | null;
  confidence: number | null;
  reasons: string[];
  retry_allowed: boolean;
  requires_human_review?: boolean;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Mutation: request a presigned upload URL for a buyer video.
 * POST /api/v2/buyer/pantene/video/upload-url
 */
export function useRequestUploadUrl() {
  return useMutation<UploadUrlResponse, Error, UploadUrlRequest>({
    mutationFn: (body) =>
      apiFetch<UploadUrlResponse>('/api/v2/buyer/pantene/video/upload-url', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

/**
 * Mutation: trigger async video analysis after file upload.
 * POST /api/v2/buyer/pantene/video/{slot_id}/analyze
 */
export function useTriggerAnalyze(slotId: string) {
  return useMutation<AnalyzeVideoResponse, Error, AnalyzeVideoRequest>({
    mutationFn: (body) =>
      apiFetch<AnalyzeVideoResponse>(
        `/api/v2/buyer/pantene/video/${slotId}/analyze`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      ),
  });
}

/**
 * Query: poll video analysis job status.
 * GET /api/v2/buyer/pantene/video/{slot_id}/status
 * Polls every 3 s until stage is 'done' or 'failed'.
 */
export function usePollStatus(slotId: string) {
  return useQuery<StatusResponse>({
    queryKey: ['pantene-status', slotId],
    queryFn: () =>
      apiFetch<StatusResponse>(
        `/api/v2/buyer/pantene/video/${slotId}/status`,
      ),
    refetchInterval: (q) => {
      const stage = q.state.data?.stage;
      return stage === 'done' || stage === 'failed' ? false : 3000;
    },
  });
}
